import { Header, MenuConfig, Sidebar } from 'alurkerja-ui';
import React, { useState } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { env } from '@/utils/configs/env';
import { useKeycloak } from '@react-keycloak/web';
import { useMenu } from '@/utils/hooks/useMenu';
import { NotificationService } from '@/api/notification';
import { UserService } from '@/api/user';

const MenuWrapper = (props: {
  children: React.ReactNode;
  menu: MenuConfig;
}) => {
  const { children, menu } = props;
  return <Link to={menu.href}>{children}</Link>;
};

export interface AvatarContentProps {
  email?: string;
  role?: string;
  onLogout: () => void;
}

export const AvatarContent: React.FC<AvatarContentProps> = ({
  email,
  role,
  onLogout,
}) => (
  <div className="p-4 min-w-[200px]">
    {(email || role) && (
      <div className="pb-3 mb-2 border-b border-gray-200">
        {email && (
          <p className="text-sm font-medium text-gray-900 break-all" title={email}>
            {email}
          </p>
        )}
        {role && (
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Role:</span>
            <span className="text-xs font-semibold text-blue-600 uppercase">
              {role}
            </span>
          </div>
        )}
      </div>
    )}
    <button
      className="w-full p-2 text-left rounded cursor-pointer hover:bg-gray-100 text-sm text-gray-700"
      onClick={onLogout}
    >
      Keluar
    </button>
  </div>
);

export const AdminLayout: React.FC = () => {
  const { keycloak } = useKeycloak();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { data: user } = UserService.useGetProjectAssignees({
    enabled: Boolean(keycloak?.authenticated),
  });

  const username = keycloak.idTokenParsed?.name;
  const userEmail = user?.email || keycloak.idTokenParsed?.email;
  const userRole = user?.user_type;
  const [toggled, setToggled] = useState(false);

  const menu = useMenu() || [];

  const { data: notifications = [] } = NotificationService.useGet();
  const markAsRead = NotificationService.useMarkAsRead();
  const markAllAsRead = NotificationService.useMarkAllAsRead();

  const unreadCount = notifications.filter(
    notification => notification.read_at === null,
  ).length;

  const notificationContent = (
    <div className="w-96 overflow-hidden rounded">
      <div className="flex w-full items-center justify-between border-b bg-white px-4 py-3">
        <p className="text-sm font-semibold">Notifikasi</p>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
          >
            Tandai semua telah dibaca
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">Tidak ada notifikasi</div>
        ) : (
          notifications.map(notification => (
            <button
              key={notification.id}
              type="button"
              onClick={() => {
                if (notification.read_at === null) {
                  markAsRead.mutate(notification.id);
                }

                if (notification.url_to) {
                  navigate(notification.url_to);
                }
              }}
              className={`flex w-full items-start gap-3 border-b px-4 py-3 text-left hover:bg-gray-50 ${
                notification.read_at === null ? 'bg-blue-50' : 'bg-white'
              }`}
            >
              {notification.read_at === null && (
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
              )}

              <div>
                <p className="text-sm font-semibold">{notification.title}</p>

                <p className="mt-1 text-xs text-gray-600">
                  {notification.message}
                </p>

                <p className="mt-1 text-[11px] text-gray-400">
                  {new Date(notification.created_at).toLocaleString('id-ID', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-screen">
      <div className="fixed z-20">
        <Sidebar
          logo={
            <div className="font-bold text-main-blue-alurkerja">
              {env.appName}
            </div>
          }
          toggled={toggled}
          setToggled={setToggled}
          menuConfig={menu}
          currentPathName={pathname}
          menuWrapper={MenuWrapper}
        />
      </div>

      <div
        className={classnames(
          'transition-[margin] ease-in-out duration-400',
          toggled ? 'sm:ml-[80px]' : 'sm:ml-[270px]',
        )}
      >
        <div className="sticky top-0 z-10 h-[8vh]">
          <Header
            role={username}
            avatarContent={
              <AvatarContent
                email={userEmail}
                role={userRole}
                onLogout={() => keycloak.logout()}
              />
            }
            showNotification
            notificationCount={unreadCount}
            notificationContent={notificationContent}
          />
        </div>

        <main className="min-h-[92vh] h-full p-4 bg-gray-200">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
